import { ApolloError, FetchResult, MutationFunctionOptions } from '@apollo/client';
import { ReactNode, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import isEmpty from 'lodash/isEmpty';

import {
  AddOrderStatus,
  CompleteCheckoutMutation as CompleteCheckout,
  CouponStatus,
  FundraisingMode,
} from '__generated__/graphql';

import { Charity } from '../../common/Fundraising/CharityPicker';
import {
  CheckoutStep, CouponCode, Event, Queue, SuccessStep, TicketCategory, getKeyedSellables, getStandAloneProducts,
  getTeamFlags, getTicketsForSale, getVisibleProducts, getVisibleProductsForTicketPromotion,
} from './helpers';
import { CookieConsentContext } from '../../common/Common/CookieConsentProvider';
import { EmbedContext } from '../Common/EmbedProvider';
import { ErrorBag, containsKeyPrefix, getServerErrors } from '../../common/helpers';
import { useCheckoutSummary } from './CheckoutSummaryProvider';
import { useEvent } from '../Common/EventProvider';
import CheckoutContext from './CheckoutContext';
import CompleteCheckoutMutation from './CompleteCheckoutMutation';
import StateContext from './StateContext';
import config from '../config';
import useCheckoutStepEventHandler from './Tracking/useCheckoutStepEventHandler';
import useCheckoutStepTracking from './Tracking/useCheckoutStepTracking';
import useCheckoutTracking from './Tracking/useCheckoutTracking';
import useFacebookPixel from '../../common/useFacebookPixel';
import useGoogleAnalytics from '../../common/useGoogleAnalytics';
import useLocale from '../../common/useLocale';
import useMemoizedValue from '../../common/useMemoizedValue';
import useMutation from '../../api/useMutation';
import useOnPageCacheRestore from '../../common/useOnPageCacheRestore';
import useOrderPlacedEventHandler from './Tracking/useOrderPlacedEventHandler';
import usePaymentMethods from '../../common/usePaymentMethods';
import usePersonalisationInfo from './usePersonalisationInfo';
import usePreselectedCharity from './usePreselectedCharity';
import useProject from '../Common/useProject';
import useQuantities from './useQuantities';
import useValidators from './useValidators';

const hasErrors = (errors: ErrorBag, activeStepName: CheckoutStep): boolean => {
  if (activeStepName === CheckoutStep.Tickets) {
    return containsKeyPrefix(errors, 'tickets')
      || containsKeyPrefix(errors, 'time_slots');
  }

  if (activeStepName === CheckoutStep.Personalisation) {
    return containsKeyPrefix(errors, 'registrations.create');
  }

  if (activeStepName === CheckoutStep.Extras) {
    return containsKeyPrefix(errors, 'registrations.stand_alone_upgrades');
  }

  if (activeStepName === CheckoutStep.Team) {
    return containsKeyPrefix(errors, 'team');
  }

  if (activeStepName === CheckoutStep.Details) {
    return containsKeyPrefix(errors, 'participant') || containsKeyPrefix(errors, 'fields');
  }

  if (activeStepName === CheckoutStep.Payment) {
    return containsKeyPrefix(errors, 'coupon');
  }

  return isEmpty(errors);
};

export interface CheckoutProps {
  event: Event;
  ticketCategories: TicketCategory[];
  checkoutSteps: CheckoutStep[];
  activeStepIndex: number;
  nextRegistration: () => boolean;
  valid: boolean[];
  completeCheckout: (options?: MutationFunctionOptions<CompleteCheckout, Record<string, any>>) => (
    Promise<FetchResult<CompleteCheckout>>
  );
  loading?: boolean;
  error?: ApolloError;
  status?: AddOrderStatus;
  preselectedCouponCode?: CouponCode;
  preselectedCharity?: Charity | null;
  queue?: Queue;
}

export interface CheckoutProviderProps {
  step?: CheckoutStep | typeof SuccessStep | null;
  render: (props: CheckoutProps) => ReactNode;
}

const CheckoutProvider = memo(({ step, render }: CheckoutProviderProps) => {
  const { locale } = useLocale();
  const { cookieConsent } = useContext(CookieConsentContext);
  const { embedded, open: embedOpen } = useContext(EmbedContext);
  const { event, ticketCategories, refetch } = useEvent();
  const history = useHistory();
  const project = useProject();

  const state = useContext(StateContext);

  const { form, session, promotionIds, setActiveRegistration, reloadState } = state;

  // Preselects a charity if a charity ID is present in the URL.
  const { preselectedCharity, hasPreselectedCharity } = usePreselectedCharity();

  /** The quantities and availabilities of tickets, products, promotions, time slots and product variants. */
  const { quantities, availability } = useQuantities({ form, event });

  /** Flags that indicate whether or not teams may be created. */
  const { allowIndividuals, allowCreateTeam, allowJoinTeam } = getTeamFlags(
    getTicketsForSale(event).filter((ticket) => quantities.tickets[ticket.id] > 0) || [],
  );

  const tickets = useMemo(() => getTicketsForSale(event), [event]);
  const keyedTickets = useMemo(() => getKeyedSellables(tickets), [tickets]);

  /** Promotions for business tickets corresponding to all registrations in the form state. */
  const selectedBusinessPromotionIds = promotionIds.filter((id) => keyedTickets[id]?.business);

  /** Allow companies if all tickets allow companies. Business tickets and consumer tickets cannot be mixed. */
  const allowCompanies = promotionIds.filter((id) => keyedTickets[id]?.allow_companies === false).length === 0;

  /** Determine if this is a preregistration. */
  const hasPreregistrations = promotionIds.filter((id) => keyedTickets[id]?.preregistration).length > 0;

  /** Determine if any business ticket has upgrades. */
  const hasVisibleBusinessProducts = selectedBusinessPromotionIds.length > 0
    && selectedBusinessPromotionIds.some((promotionId) => (
      getVisibleProductsForTicketPromotion({ id: promotionId }, event.products_for_sale).length > 0
    ));

  /** All products that can be bought independently of tickets. */
  const standAloneProducts = useMemo(() => (
    getStandAloneProducts(promotionIds.map((id) => ({ id })), [], event.products_for_sale)
  ), [promotionIds, event.products_for_sale]);

  /** Info about which registrations are personalisable, skipped, etc. */
  const personalisation = usePersonalisationInfo({ form, event, session, hasPreselectedCharity });

  /** The personalisation step should be shown if there are personalisable registrations. */
  const showPersonalisationStep = personalisation.personalisableRegistrations.length > 0;

  /** Show the extras if there are selectable business tickets or stand-alone products. */
  const showExtrasStep = hasVisibleBusinessProducts || getVisibleProducts(standAloneProducts).length > 0;

  /** Hide the details step for preregistrations if there are no other fields. */
  const hideDetailsStep = hasPreregistrations && form.fields.length === 0 && !allowCompanies;

  /** Show the team form if creating or joining teams is allowed. */
  const showTeamStep = (allowCreateTeam || allowJoinTeam) && !event.invitation_code?.invitation.team;

  /** Show the fundraising form if fundraising is allowed. */
  const showFundraisingStep = !hasPreselectedCharity
    // Fundraising is enabled
    && event.enable_fundraising
    // Fundraiser may be creatuing during registration
    && event.fundraising_mode === FundraisingMode.during_registration
    // If fundraising is required and there is exactly 1 charity to choose from, don't show the form.
    && !(event.require_fundraising && !event.allow_other_charity && event.charities.length === 1)
    // Only show it if buying personal tickets (that are not linked to a charity already).
    && personalisation.personalRegistrations.filter(
      (registration) => !keyedTickets[registration.purchase.promotion.id]?.charity,
    ).length > 0;

  const checkoutSteps = useMemo(() => [
    CheckoutStep.Tickets,
    ...(showPersonalisationStep ? [CheckoutStep.Personalisation] : []),
    ...(showExtrasStep ? [CheckoutStep.Extras] : []),
    ...(showTeamStep ? [CheckoutStep.Team] : []),
    ...(!hideDetailsStep ? [CheckoutStep.Details] : []),
    ...(showFundraisingStep ? [CheckoutStep.Fundraising] : []),
    CheckoutStep.Payment,
  ], [showPersonalisationStep, showExtrasStep, hideDetailsStep, showTeamStep, showFundraisingStep]);

  const activeStepName = step || checkoutSteps[0];
  const activeStepIndex = checkoutSteps.indexOf(activeStepName as CheckoutStep);

  // Load trackers
  const { trackEvent, trackPageView } = useGoogleAnalytics({
    propertyIds: [config.googleAnalyticsId, project.google_analytics].filter((propertyId) => propertyId),
    enabled: !embedded,
  });

  const fbq = useFacebookPixel({
    enabled: !embedded && cookieConsent,
    pixelId: project.facebook_pixel,
  });

  useEffect(() => {
    // Ensure that Clarity is loaded (it's not loaded in tests).
    if ('clarity' in window) {
      // Pass cookie consent.
      Clarity.consent(cookieConsent);
    }
  }, [cookieConsent]);

  // Dispatch and handle tracking events
  useCheckoutStepTracking(activeStepName);
  useCheckoutStepEventHandler({
    fbq,
    trackPageView,
  });

  useOrderPlacedEventHandler({
    fbq,
    trackEvent,
    trackPageView,
  });

  const requireInvoiceDetails = form.registrations.business.length > 0;
  const invoiceDetails = session.invoiceDetails || requireInvoiceDetails ? form.invoice : null;

  // Used to disable the 'Pay' button while the browser is redirecting the user, to
  // prevent the user from creating another payment in the mean time.
  // Unfortunately we can't simply disable the button if completeCheckoutData is present,
  // because this state object is restored by Safari on pressing the browser back button.
  // In that case, the user should be able to resubmit the checkout.
  const [redirecting, setRedirecting] = useState(false);

  useOnPageCacheRestore(() => {
    // Enable Pay button
    setRedirecting(false);

    // Reload checkout state from Session Storage,
    // because it might have been reset on the Thank you page.
    reloadState();
  });

  const [
    completeCheckout, { error, loading: submitting, data: completeCheckoutData },
  ] = useMutation(
    CompleteCheckoutMutation,
    {
      variables: {
        input: {
          ...form,
          participant: {
            ...form.participant, // Contains mail opt-ins
            email: personalisation.buyer.email,
            first_name: personalisation.buyer.first_name,
            last_name: personalisation.buyer.last_name,
          },
          registrations: {
            create: form.registrations.create.map((registration, index) => ({
              ...registration,
              // Unset the assignee fields when the registration is unassigned or skipped.
              ...(!registration.participant || !personalisation.personalisedFormIndices.includes(index) ? {
                participant: null,
                details: null,
                fields: null,
              } : {}),
            })),
            business: form.registrations.business,
            stand_alone_upgrades: form.registrations.stand_alone_upgrades,
          },
          invoice: invoiceDetails,
          payment_method: form.payment_method.payment_method ? form.payment_method : null,
          team: form.team,
          // URL to redirect back to the site when using the embed script.
          source_url: form.source_url,
          initial_url: form.initial_url,
          final_url: form.final_url,
          locale,
        },
      },
      onCompleted: ({ completeCheckout: { redirect_url, status } }) => {
        if (status === AddOrderStatus.SUCCESS) {
          if (redirect_url) {
            // Disable the 'Pay' button while redirecting.
            setRedirecting(true);

            if (embedded) {
              // Use window.parent, because in Cypress window.top is the spec runner window.
              window.parent.location.href = redirect_url;
            } else {
              window.location.href = redirect_url;
            }
          }
        }
      },
      onError: (apolloError) => {
        const serverErrors = getServerErrors(apolloError);

        const errorStepName = checkoutSteps.filter(
          (stepName) => hasErrors(serverErrors, stepName),
        )[0];

        if (errorStepName) {
          history.push(`/${event.id}/${errorStepName}`);
        }

        // Refetch event, tickets might be sold out (refetchQueries doesn't run on error)
        refetch();

        throw new Error();
      },
    },
  );

  const loading = submitting || redirecting;

  const status = completeCheckoutData?.completeCheckout.status;

  const { checkoutSummary } = useCheckoutSummary();

  const paymentMethods = usePaymentMethods({
    // Do not take country from the invoice details if it's only present because it has been pre-filled.
    country: (invoiceDetails?.zip_code && invoiceDetails?.country)
      || personalisation.personalRegistrations[0]?.details?.country,
    enabledPaymentMethods: event.payment_methods,
    bankTransferAllowed: !!invoiceDetails?.company_name || !event.require_company_for_bank_transfer,
  });

  const validators = useValidators({
    form,
    session,
    event,
    personalisation,
    standAloneProducts,
    requireInvoiceDetails,
    checkoutSummary,
    paymentMethods,
    error,
  });

  /**
   * An array whose elements indicate whether the checkout step is valid. As
   * soon as one step is found to be invalid, all future steps become invalid.
   * The memoization ensures that a new object is only returned when any of
   * the flags change. Without this memoization, the entire CheckoutPage would
   * rerender on each change event (ticket added, etc.).
   */
  const valid = useMemoizedValue(checkoutSteps.reduce((valid, name) => (
    [...valid, !valid.includes(false) && validators[name].valid]
  ), [] as boolean[]));

  const shouldGoToNextRegistration = activeStepName === CheckoutStep.Personalisation
    && personalisation.nextIndex < personalisation.personalisableRegistrations.length;

  const nextRegistration = useCallback(() => {
    if (shouldGoToNextRegistration) {
      setActiveRegistration(personalisation.nextIndex);

      return true;
    }

    return false;
  }, [shouldGoToNextRegistration, personalisation.nextIndex, setActiveRegistration]);

  const preselectedCouponCode = checkoutSummary.coupon_status === CouponStatus.active
    ? checkoutSummary.coupon_code : undefined;

  /**
   * By memoizing the entire context value, we prevent that children get rerendered when this component rerenders,
   * if the elements of the context value did not change.
   */
  const context = useMemo(
    () => ({
      ...state,
      standAloneProducts,
      personalisation,
      allowIndividuals,
      allowCreateTeam,
      allowJoinTeam,
      allowCompanies,
      hasPreregistrations,
      requireInvoiceDetails,
      validators,
      quantities,
      availability,
      error,
      paymentMethods,
    }),
    [
      state,
      standAloneProducts,
      personalisation,
      allowIndividuals,
      allowCreateTeam,
      allowJoinTeam,
      allowCompanies,
      hasPreregistrations,
      requireInvoiceDetails,
      validators,
      quantities,
      availability,
      error,
      paymentMethods,
    ],
  );

  useCheckoutTracking({
    form,
    queue: checkoutSummary.queue,
    step: activeStepName,
    stepIndex: activeStepIndex,
    valid,
    personalisation,
    embedded,
    embedOpen,
  });

  // Make sure that these props are memoized to prevent unnecessary rerenders
  const props = {
    event,
    ticketCategories,
    checkoutSteps,
    activeStepIndex,
    nextRegistration,
    completeCheckout,
    loading,
    error,
    status,
    valid,
    preselectedCharity,
    preselectedCouponCode,
    queue: checkoutSummary.queue,
  };

  return (
    <CheckoutContext.Provider value={context}>
      {render(props)}
    </CheckoutContext.Provider>
  );
});

CheckoutProvider.displayName = 'CheckoutProvider';

export default CheckoutProvider;
